(() => {
  function aggregateUrl(subscriberId, source) {
    if (typeof subscriberId !== 'string' || !subscriberId.trim()) return null;
    const base = `https://api.okendo.io/v1/stores/${encodeURIComponent(subscriberId)}`;
    if (/^shopify-\d+$/.test(source)) return `${base}/products/${source}/review_aggregate`;
    if (/^group-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(source)) {
      return `${base}/collections/${source.slice(6)}/review_aggregate`;
    }
    return null;
  }

  function readAggregate(payload, source, subscriberId) {
    const aggregate = payload?.reviewAggregate;
    if (!aggregate || typeof source !== 'string') return null;
    if (source.startsWith('group-')) {
      const groupId = source.slice(6);
      if (!aggregateUrl(subscriberId, source) || aggregate.productId
        || (aggregate.groupId && aggregate.groupId !== groupId)
        || aggregate.subscriberId_aggregateId !== `${subscriberId}:${groupId}`) return null;
    } else if (!/^shopify-\d+$/.test(source) || aggregate.productId !== source || aggregate.groupId) return null;
    const count = aggregate.reviewCount;
    const total = aggregate.reviewRatingValuesTotal;
    if (!Number.isSafeInteger(count) || count <= 0 || !Number.isFinite(total) || total < count || total > count * 5) return null;
    return { count, average: total / count };
  }

  function qualifies(aggregate, minimum) {
    return Boolean(aggregate && Number.isSafeInteger(minimum) && minimum >= 0 && aggregate.count >= minimum);
  }

  function submissionUrl(href, actualProduct) {
    if (!/^shopify-\d+$/.test(actualProduct)) return null;
    try {
      const url = new URL(href);
      if (url.origin !== 'https://okendo.reviews' || !url.searchParams.has('productId')) return null;
      url.searchParams.set('productId', actualProduct);
      return url.href;
    } catch { return null; }
  }

  function readConfig(text) {
    try {
      const config = JSON.parse(text);
      if (!config || !Number.isSafeInteger(config.minimum) || config.minimum < 0) return null;
      for (const key of ['subscriberId', 'countLabel', 'linkLabel', 'sectionLabel']) {
        if (typeof config[key] !== 'string' || !config[key].trim()) return null;
      }
      return config;
    } catch { return null; }
  }

  function createFormatters(locale) {
    const options = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
    try {
      return { number: new Intl.NumberFormat(locale || undefined), rating: new Intl.NumberFormat(locale || undefined, options) };
    } catch {
      return { number: new Intl.NumberFormat('en'), rating: new Intl.NumberFormat('en', options) };
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { readAggregate, qualifies, submissionUrl, readConfig, createFormatters, aggregateUrl };
  }
  if (typeof document === 'undefined') return;

  const configElement = document.getElementById('bb-review-config');
  const config = readConfig(configElement?.textContent);
  const minimum = config?.minimum;
  const requests = new Map();
  const results = new Map();
  const { number, rating } = createFormatters(document.documentElement.lang);
  const observedPools = new WeakSet();

  function apply() {
    document.querySelectorAll('[data-bb-review-summary]').forEach(element => {
      const source = element.dataset.bbReviewSource;
      const result = results.get(source);
      const target = [...document.querySelectorAll('[data-bb-review-pool]')].find(pool => pool.dataset.bbReviewSource === source);
      const visible = Boolean(config) && qualifies(result, minimum)
        && Boolean(target?.querySelector('[data-oke-rendered]') && target.querySelector('[data-oke-container]'));
      if (visible) {
        const count = number.format(result.count);
        const average = rating.format(result.average);
        element.querySelector('[data-bb-review-average]').textContent = average;
        element.querySelector('[data-bb-review-count]').textContent = config.countLabel.replace('[count]', count);
        element.querySelector('[data-bb-review-stars]').style.width = `${result.average / 5 * 100}%`;
        element.setAttribute('aria-label', config.linkLabel.replace('[count]', count).replace('[rating]', average));
      }
      element.hidden = !visible;
    });
    document.querySelectorAll('[data-bb-review-pool]').forEach(pool => {
      const visible = qualifies(results.get(pool.dataset.bbReviewSource), minimum);
      pool.toggleAttribute('data-bb-review-qualified', visible);
      const region = pool.querySelector('[data-oke-container][role="region"]');
      const sectionLabel = config?.sectionLabel || pool.dataset.bbReviewSectionLabel;
      if (region && !visible && sectionLabel && region.getAttribute('aria-label') !== sectionLabel) {
        region.setAttribute('aria-label', sectionLabel);
      }
      pool.querySelectorAll('[data-oke-widget] a.oke-w-writeReview').forEach(link => {
        const url = submissionUrl(link.href, pool.dataset.bbActualProduct);
        if (url && link.href !== url) link.href = url;
        link.toggleAttribute('data-bb-submission-ready', Boolean(url));
      });
    });
  }

  function load() {
    observePools();
    document.querySelectorAll('[data-bb-review-source]').forEach(element => {
      const source = element.dataset.bbReviewSource;
      const url = config && aggregateUrl(config.subscriberId, source);
      if (!url || requests.has(source)) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      const request = fetch(url, { signal: controller.signal, cache: 'no-cache' })
        .then(response => response.ok ? response.json() : Promise.reject(new Error('Review data unavailable')))
        .then(payload => results.set(source, readAggregate(payload, source, config.subscriberId)))
        .catch(() => results.set(source, null))
        .finally(() => { clearTimeout(timeout); apply(); });
      requests.set(source, request);
    });
    apply();
  }

  function observePools() {
    document.querySelectorAll('[data-bb-review-pool]').forEach(pool => {
      if (observedPools.has(pool)) return;
      const observer = new MutationObserver(records => {
        if (records.some(record => record.attributeName === 'data-oke-rendered'
          || (record.attributeName === 'href' && record.target.matches('a.oke-w-writeReview'))
          || [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1))) apply();
      });
      observer.observe(pool, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-oke-rendered', 'href'] });
      observedPools.add(pool);
    });
  }

  const root = document.getElementById('MainContent') || document.body;
  if (root) {
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1
        && (node.matches('[data-bb-review-source]') || node.querySelector('[data-bb-review-source]'))))) load();
    });
    observer.observe(root, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:section:load', load);
  document.addEventListener('click', event => {
    const summary = event.target.closest('[data-bb-review-summary]');
    if (!summary || summary.hidden) return;
    document.getElementById('bb-product-reviews')?.focus({ preventScroll: true });
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { requests.clear(); results.clear(); load(); }
  });
  load();
})();
